import React, { useEffect, useRef, useState } from 'react';
import { Lock } from 'lucide-react';
import { toast } from 'react-toastify';
import { Action, Banner, Chip, ErrorNote, Panel, PanelHead, SettingRow, Toggle } from '../aa';
import { billingApi, type BillingCard, type InvoicePage, type WalletSummary } from '../../services/billingApi';
import { AmountText, InvoiceStatus, KindMark, invoiceAmount, invoiceDate } from './invoiceParts';

const money = (cents: number) => `$${(Math.abs(cents) / 100).toFixed(2)}`;

const wholeDollars = (cents: number) => `$${(cents / 100).toLocaleString()}`;

const FUND_AMOUNTS = [2500, 5000, 10000];
const CUSTOM_MIN_CENTS = 500;
const CUSTOM_MAX_CENTS = 1_000_000;

/* Stripe's payment page sends the Owner back through /settings, whose
 * redirect drops query params, so the session id waits here until the panel
 * mounts again and can credit the balance without waiting for the webhook. */
const PENDING_TOPUP = 'aa_pending_topup_session';

const readPendingTopup = (): string | null => {
  try {
    return sessionStorage.getItem(PENDING_TOPUP);
  } catch {
    return null;
  }
};

const writePendingTopup = (sessionId: string | null) => {
  try {
    if (sessionId) {
      sessionStorage.setItem(PENDING_TOPUP, sessionId);
      return;
    }
    sessionStorage.removeItem(PENDING_TOPUP);
  } catch {
    /* storage blocked: the webhook still credits the balance */
  }
};

/* Turning on automatic top-up with no card sends the Owner to Stripe's card
 * page first. The chosen top-up rule waits here until they come back. */
const PENDING_CARD = 'aa_pending_card_setup';

type PendingCard = { sessionId: string; thresholdCents: number; amountCents: number; cardOnly?: boolean };

const readPendingCard = (): PendingCard | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_CARD);
    if (!raw) return null;
    return JSON.parse(raw) as PendingCard;
  } catch {
    return null;
  }
};

const writePendingCard = (pending: PendingCard | null) => {
  try {
    if (pending) {
      sessionStorage.setItem(PENDING_CARD, JSON.stringify(pending));
      return;
    }
    sessionStorage.removeItem(PENDING_CARD);
  } catch {
    /* storage blocked: the Owner turns automatic top-up on again after the card is saved */
  }
};

const TOPUP_REFUSED = ['card_declined', 'payment_incomplete', 'payment_method_required', 'stripe_error'];

/* A save checks the balance at once; say what that check did. */
const announceTopup = (data: WalletSummary) => {
  if (data.topup === 'topped_up') {
    toast.success(`Your balance was at or under the threshold, so ${money(data.topup_amount_cents ?? 0)} was added.`);
    return;
  }
  if (data.topup && TOPUP_REFUSED.includes(data.topup)) {
    toast.error('Your balance is low, but the top-up payment did not go through. Check your card and try again.');
  }
};

/* Back from Stripe's card page: record the card, then turn automatic top-up
 * on with the rule the Owner chose. Runs before Billing loads so the new card
 * shows at once. */
export const finishCardSetup = async (): Promise<void> => {
  const pending = readPendingCard();
  if (!pending) return;
  writePendingCard(null);
  try {
    const { data } = await billingApi.confirmCardSession(pending.sessionId);
    if (pending.cardOnly) {
      if (data.card) toast.success(`${cardName(data.card)} is saved as your card.`);
      return;
    }
    if (!data.card) {
      toast.info('No card was saved, so automatic top-up is still off.');
      return;
    }
    await billingApi.setTopupRule(pending.thresholdCents, pending.amountCents);
    const { data: wallet } = await billingApi.setAutoTopup(true);
    toast.success('Card saved. Automatic top-up is on.');
    announceTopup(wallet);
  } catch {
    toast.error('Could not finish turning on automatic top-up. Try the switch again.');
  }
};

export const cardName = (card: BillingCard): string => {
  const brand = card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : 'your card';
  if (!card.last4) return brand;
  return `${brand} ending ${card.last4}`;
};

/* Stripe's card page for the Owner's own card, with nothing else to finish
 * on the way back. */
export const startOwnCardSetup = async (): Promise<void> => {
  const back = `${window.location.origin}/settings?tab=payment`;
  const { data } = await billingApi.startCardSession(back, back);
  writePendingCard({ sessionId: data.session_id, thresholdCents: 0, amountCents: 0, cardOnly: true });
  window.location.assign(data.url);
};

/* The wallet as Billing first shows it. A finished top-up page is credited
 * first, so the balance on screen already includes the payment. */
export const loadWallet = async (): Promise<WalletSummary | null> => {
  const pending = readPendingTopup();
  if (pending) {
    writePendingTopup(null);
    try {
      const { data } = await billingApi.confirmTopup(pending);
      if (data.paid_cents > 0) toast.success(`${money(data.paid_cents)} added to your prepaid balance.`);
      return data;
    } catch {
      /* the webhook credits it; the read below shows whatever is there */
    }
  }
  try {
    const { data } = await billingApi.getWallet();
    return data;
  } catch {
    return null;
  }
};

const day = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

const dollarsText = (cents: number | undefined) => ((cents ?? 0) / 100).toFixed(2);

const toCents = (text: string) => Math.round(parseFloat(text || '0') * 100);

const MoneyField: React.FC<{
  id: string;
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
}> = ({ id, label, hint, value, onChange, disabled }) => (
  <div>
    <label htmlFor={id} className="block text-[13px] font-semibold text-ink">{label}</label>
    <div className="mt-1.5 flex h-11 items-center rounded-act border border-line-strong bg-white pl-3 focus-within:border-ink focus-within:ring-[3px] focus-within:ring-ink/10">
      <span className="text-[14px] text-ink-dim">$</span>
      <input
        id={id}
        inputMode="decimal"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9.]/g, ''))}
        disabled={disabled}
        className="h-full w-full border-0 bg-transparent px-2 text-[14px] tabular-nums text-ink shadow-none outline-none focus:outline-none focus:ring-0"
      />
    </div>
    <div className="mt-1.5 text-[12px] text-ink-dim">{hint}</div>
  </div>
);

/* The Owner's own rule: when the prepaid balance falls below the threshold,
 * the saved card is charged the top-up amount. */
const TopupRule: React.FC<{
  wallet: WalletSummary;
  onWallet: (wallet: WalletSummary) => void;
}> = ({ wallet, onWallet }) => {
  const [threshold, setThreshold] = useState(dollarsText(wallet.topup_threshold_cents));
  const [amount, setAmount] = useState(dollarsText(wallet.topup_amount_cents));
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    setThreshold(dollarsText(wallet.topup_threshold_cents));
    setAmount(dollarsText(wallet.topup_amount_cents));
  }, [wallet.topup_threshold_cents, wallet.topup_amount_cents]);

  const thresholdCents = toCents(threshold);
  const amountCents = toCents(amount);
  const changed = thresholdCents !== (wallet.topup_threshold_cents ?? 0) || amountCents !== (wallet.topup_amount_cents ?? 0);

  let problem = '';
  if (thresholdCents > CUSTOM_MAX_CENTS) problem = `The threshold can be at most ${wholeDollars(CUSTOM_MAX_CENTS)}.`;
  if (amountCents < CUSTOM_MIN_CENTS || amountCents > CUSTOM_MAX_CENTS) problem = `The top-up amount must be between ${wholeDollars(CUSTOM_MIN_CENTS)} and ${wholeDollars(CUSTOM_MAX_CENTS)}.`;
  if (!problem && amountCents <= thresholdCents) problem = 'The top-up amount must be more than the threshold.';

  const save = async () => {
    if (problem || !changed || inFlight.current) return;
    inFlight.current = true;
    setSaving(true);
    try {
      const { data } = await billingApi.setTopupRule(thresholdCents, amountCents);
      onWallet({ ...wallet, ...data });
      toast.success('Automatic top-up updated.');
      announceTopup(data);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      let message = 'Could not save the top-up settings.';
      if (typeof detail === 'string') message = detail;
      toast.error(message);
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  };

  let reason = 'Change a value to save.';
  if (problem) reason = problem;
  if (saving) reason = 'Saving';

  return (
    <div className="border-t border-line-soft px-6 pb-6 pt-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <MoneyField id="topup-threshold" label="Top up when balance falls below (USD)" hint="Checked after each charge."
          value={threshold} onChange={setThreshold} disabled={saving} />
        <MoneyField id="topup-amount" label="Top-up amount (USD)" hint="Charged to your saved card. Must be more than the threshold."
          value={amount} onChange={setAmount} disabled={saving} />
      </div>
      {changed && problem && <div role="alert" className="mt-3 text-[12px] text-danger">{problem}</div>}
      <div className="mt-4 flex justify-end">
        <Action onClick={save} disabled={saving || !changed || !!problem} reason={reason}>
          {saving ? 'Saving…' : 'Save top-up settings'}
        </Action>
      </div>
    </div>
  );
};

/* This period's agent usage: included usage first, then the prepaid
 * balance, then work pauses. The live dollar meter the Owner pays against. */
export const UsageWalletPanel: React.FC<{
  wallet: WalletSummary | null;
  onWallet: (wallet: WalletSummary) => void;
  card?: BillingCard | null;
  viewerEmail?: string;
}> = ({ wallet, onWallet, card = null, viewerEmail = '' }) => {
  const [busy, setBusy] = useState(false);
  const [choice, setChoice] = useState<number | 'custom'>(FUND_AMOUNTS[0]);
  const [custom, setCustom] = useState('');
  const [autoBusy, setAutoBusy] = useState(false);
  // State updates land after a re-render; a fast double click lands before it.
  const inFlight = useRef(false);

  if (!wallet) return <ErrorNote message="Couldn't load your usage. Refresh to try again." />;
  if (!wallet.billed || !wallet.can_manage) return null;

  const used = wallet.used_cents ?? 0;
  const allowance = wallet.allowance_cents ?? 0;
  const balance = wallet.balance_cents ?? 0;
  const remaining = wallet.remaining_cents ?? 0;

  const customCents = Math.round(parseFloat(custom || '0') * 100);
  const customValid = customCents >= CUSTOM_MIN_CENTS && customCents <= CUSTOM_MAX_CENTS;
  const amount = choice === 'custom' ? customCents : choice;
  const amountValid = choice !== 'custom' || customValid;

  const pay = async () => {
    if (!amountValid || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    const back = `${window.location.origin}/settings?tab=payment`;
    try {
      const { data } = await billingApi.startTopup(amount, back, back);
      writePendingTopup(data.session_id);
      window.location.assign(data.url);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      let message = 'Could not open the payment page. Try again.';
      if (typeof detail === 'string') message = detail;
      toast.error(message);
      inFlight.current = false;
      setBusy(false);
    }
  };

  const addCardForAuto = async () => {
    inFlight.current = true;
    setAutoBusy(true);
    const back = `${window.location.origin}/settings?tab=payment`;
    try {
      const { data } = await billingApi.startCardSession(back, back);
      writePendingCard({
        sessionId: data.session_id,
        thresholdCents: wallet.topup_threshold_cents ?? 0,
        amountCents: wallet.topup_amount_cents ?? 0,
      });
      window.location.assign(data.url);
    } catch {
      toast.error('Could not open the card page. Try again.');
      inFlight.current = false;
      setAutoBusy(false);
    }
  };

  const toggleAuto = async (enabled: boolean) => {
    if (inFlight.current) return;
    if (enabled && !card) {
      await addCardForAuto();
      return;
    }
    inFlight.current = true;
    setAutoBusy(true);
    try {
      const { data } = await billingApi.setAutoTopup(enabled);
      onWallet({ ...wallet, ...data });
      announceTopup(data);
    } catch {
      toast.error('Could not change automatic top-up.');
    } finally {
      inFlight.current = false;
      setAutoBusy(false);
    }
  };

  const threshold = money(wallet.topup_threshold_cents ?? 0);
  const topup = money(wallet.topup_amount_cents ?? 0);
  const payer = wallet.topup_payer_email || '';
  const paidByOther = !!payer && payer !== viewerEmail;
  const autoOn = !!wallet.auto_topup && (!!card || paidByOther);
  let autoDesc = `Turn on to add a card on Stripe's secure page. When the balance falls below ${threshold}, it is charged ${topup} so work never stops. Card details stay with Stripe.`;
  if (card) autoDesc = `When the balance falls below ${threshold}, charge ${topup} to ${cardName(card)} so work never stops.`;
  if (autoOn && paidByOther) autoDesc = `When the balance falls below ${threshold}, ${payer}'s card is charged ${topup}. Changing it moves the charge to your own card.`;

  let customError = '';
  if (choice === 'custom' && custom && !customValid) customError = `Enter between ${wholeDollars(CUSTOM_MIN_CENTS)} and ${wholeDollars(CUSTOM_MAX_CENTS)}.`;

  let balanceText = money(balance);
  let balanceNote = 'Carries over every month';
  if (balance < 0) {
    balanceText = `-${money(balance)}`;
    balanceNote = 'Owed, taken from your next included usage';
  }

  let head = (
    <PanelHead
      kicker="Usage"
      title={`${money(used)} of agent usage since ${day(wallet.period_start || new Date().toISOString())}`}
      desc="Included usage is spent first, then your prepaid balance. When both run out, agent work pauses instead of running up a bill."
    />
  );
  if (!wallet.usage_available) {
    head = <PanelHead kicker="Usage" title="Agent usage" desc="Usage figures are being refreshed. Check back in a minute." />;
  }

  return (
    <Panel>
      {head}
      <div className="space-y-5 px-6 pb-6 pt-2">
        {wallet.paused && (
          <Banner tone="surface" title="Agent work is paused" desc="Add funds to your prepaid balance to start again." />
        )}
        {wallet.usage_available && (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-panel bg-canvas-inset px-4 py-3.5">
                <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-eyebrow">Included usage</div>
                <div className="mt-1 text-[20px] font-bold leading-[26px] tabular-nums text-ink">
                  {money(Math.max(allowance - (wallet.allowance_used_cents ?? 0), 0))}
                  <span className="ml-1 text-[12.5px] font-medium text-ink-dim">left of {money(allowance)}</span>
                </div>
                {allowance > 0 && (
                  <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-white" role="progressbar" aria-label="Included usage spent"
                    aria-valuemin={0} aria-valuemax={allowance} aria-valuenow={wallet.allowance_used_cents ?? 0}>
                    <div className="h-full rounded-full bg-ink" style={{ width: `${Math.min(100, ((wallet.allowance_used_cents ?? 0) / allowance) * 100)}%` }} />
                  </div>
                )}
                {allowance === 0 && <div className="mt-1 text-[12px] text-ink-dim">Comes with Team plan seats</div>}
              </div>
              <div className="rounded-panel bg-canvas-inset px-4 py-3.5">
                <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-eyebrow">Prepaid balance</div>
                <div className="mt-1 text-[20px] font-bold leading-[26px] tabular-nums text-ink">{balanceText}</div>
                <div className="mt-1 text-[12px] text-ink-dim">{balanceNote}</div>
              </div>
              <div className="rounded-panel bg-canvas-inset px-4 py-3.5">
                <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-eyebrow">Left before work pauses</div>
                <div className="mt-1 text-[20px] font-bold leading-[26px] tabular-nums text-ink">{money(Math.max(remaining, 0))}</div>
                <div className="mt-1 flex items-center gap-2 text-[12px] text-ink-dim">
                  Included plus prepaid
                  {!wallet.enforced && <Chip tone="private">Not enforced yet</Chip>}
                </div>
              </div>
            </div>
            <div className="text-[12.5px] text-ink-dim">
              This period: AI usage {money(wallet.ai_cents ?? 0)} · Workspace compute {money(wallet.compute_cents ?? 0)}
            </div>
          </>
        )}
        <div className={wallet.usage_available ? 'border-t border-line-soft pt-5' : ''}>
          <div className="mb-3 text-[13px] font-semibold text-ink">Add funds</div>
          <div role="radiogroup" aria-label="Amount to add" className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[...FUND_AMOUNTS, 'custom' as const].map((option) => {
              const selected = choice === option;
              let label = 'Custom';
              if (option !== 'custom') label = wholeDollars(option);
              return (
                <button
                  key={String(option)}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={busy}
                  onClick={() => setChoice(option)}
                  className={`h-12 rounded-act border text-[15px] font-semibold tabular-nums transition-colors disabled:cursor-not-allowed ${
                    selected
                      ? 'border-ink bg-canvas-inset text-ink ring-[3px] ring-ink/10'
                      : 'border-line-strong bg-white text-ink hover:bg-canvas-inset'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {choice === 'custom' && (
            <div className="mt-3">
              <div className="flex h-11 items-center rounded-act border border-line-strong bg-white pl-3 focus-within:border-ink focus-within:ring-[3px] focus-within:ring-ink/10 sm:max-w-[260px]">
                <span className="text-[14px] text-ink-dim">$</span>
                <input
                  autoFocus
                  aria-label="Custom amount in dollars"
                  inputMode="decimal"
                  placeholder={`${CUSTOM_MIN_CENTS / 100} to ${(CUSTOM_MAX_CENTS / 100).toLocaleString()}`}
                  value={custom}
                  onChange={(e) => setCustom(e.target.value.replace(/[^0-9.]/g, ''))}
                  onKeyDown={(e) => { if (e.key === 'Enter') pay(); }}
                  disabled={busy}
                  className="h-full w-full border-0 bg-transparent px-2 text-[14px] text-ink shadow-none outline-none focus:outline-none focus:ring-0"
                />
              </div>
              {customError && <div role="alert" className="mt-1.5 text-[12px] text-danger">{customError}</div>}
            </div>
          )}
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <span className="flex items-center gap-1.5 text-[12px] text-ink-dim">
              <Lock size={13} /> Paid on Stripe's secure page, then you come straight back here.
            </span>
            <Action onClick={pay} disabled={busy || !amountValid}
              reason={busy ? 'Opening the payment page' : `Enter between ${wholeDollars(CUSTOM_MIN_CENTS)} and ${wholeDollars(CUSTOM_MAX_CENTS)}.`}>
              {amountValid && amount > 0 ? `Add ${money(amount)} to balance` : 'Add to balance'}
            </Action>
          </div>
        </div>
      </div>
      <SettingRow
        className="border-t border-line-soft"
        title="Automatic top-up"
        desc={autoDesc}
        control={<Toggle checked={autoOn} onChange={toggleAuto} label="Automatic top-up" disabled={autoBusy} />}
      />
      {autoOn && <TopupRule wallet={wallet} onWallet={onWallet} />}
    </Panel>
  );
};

export const RECENT_INVOICES = 4;

/* The latest few in Billing; every invoice lives on the Invoices page. */
export const InvoicesPanel: React.FC<{ page: InvoicePage | null }> = ({ page }) => {
  const invoices = (page?.invoices || []).slice(0, RECENT_INVOICES);
  const hasMore = (page?.invoices.length || 0) > RECENT_INVOICES || !!page?.has_more;

  let action: React.ReactNode = null;
  if (invoices.length > 0) {
    action = (
      <Action size="sm" variant="outline" to="/billing/invoices" arrow>
        {hasMore ? 'View all invoices' : 'Open invoices'}
      </Action>
    );
  }

  let empty = 'No invoices yet. Your first one appears when you add funds or move to the Team plan.';
  if (!page) empty = 'Invoices could not be loaded from Stripe right now. Reopen Billing to try again.';

  return (
    <Panel>
      <PanelHead kicker="Receipts" title="Recent invoices" desc="Your plan, seat changes and top-ups. Every receipt is on the Invoices page." action={action} />
      {invoices.length === 0 && <div className="px-6 pb-6 text-[13px] text-ink-dim">{empty}</div>}
      {invoices.length > 0 && (
        <ul className="m-0 list-none border-t border-line-soft p-0">
          {invoices.map((inv) => (
            <li key={inv.id} className="border-b border-line-soft last:border-b-0">
              <a
                href={inv.url || undefined}
                target="_blank"
                rel="noreferrer"
                aria-label={`${inv.title}, ${invoiceAmount(inv.total_cents)}, ${invoiceDate(inv.created)}. Opens the receipt`}
                className="flex min-h-[60px] items-center gap-3 px-6 py-3 transition-colors hover:bg-canvas"
              >
                <KindMark kind={inv.kind} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-semibold text-ink">{inv.title}</span>
                  <span className="block truncate text-[12px] text-ink-dim">{invoiceDate(inv.created)} · {inv.detail}</span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-0.5">
                  <AmountText cents={inv.total_cents} className="text-[13.5px] font-semibold" />
                  <InvoiceStatus invoice={inv} />
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
};
